// ---------------------------------------------------------------------------
// ปิดบิลเมื่อรับเงินสำเร็จ (ใช้ร่วม: แอดมินรับชำระ / ลูกค้าจ่ายใน /app / หน้าสาธารณะ /pay/:code)
// ---------------------------------------------------------------------------
// settleInvoicePayment กันซ้ำด้วย transactionId — เรียกซ้ำ (poll ซ้ำ / กดลองบันทึกใหม่ / เปิดสองแท็บ) ได้ผลเดิม
//   1. ใบแจ้งหนี้ -> paid  2. ขอเลขใบเสร็จ + สร้าง receipt (transactionId, invoiceId)
//   3. ปิด payment link ของใบแจ้งหนี้นี้  4. ร้านมี e-Tax -> สร้างเอกสารภาษี "ฉบับร่าง" (ร้านตรวจแล้วกดออกเอง)
// ทุกขั้นทำซ้ำได้ ถ้าพังกลางทางแล้วเรียกใหม่ด้วย transactionId เดิมจะทำส่วนที่ค้างต่อ
// ยอดที่ชำระต้องเท่ายอดบิลเป๊ะ (ไม่รองรับจ่ายบางส่วน)
// ---------------------------------------------------------------------------
import { invoiceRepo, receiptRepo, paymentLinkRepo, organizationRepo } from "../mock/repositories";
import { nextDocNo } from "./docNumber";
import { logActivity } from "./activityLog";
import { taxDocumentService } from "./taxDocument";
import { resolveSubscription } from "../config/plans";
import { CORE_MODULE_KEYS } from "../config/feature-modules";
import { earnPoints } from "./loyalty";

const sameAmount = (a, b) => Math.abs(Number(a) - Number(b)) < 0.005;

/** ร้านใช้ e-Tax ได้ไหม (อยู่ในแพ็กเกจ/ฟีเจอร์เสริมที่ยังไม่หมดอายุ + เปิดโมดูลไว้ + จด VAT) */
export const orgUsesEtax = (organization) => {
  if (!organization?.vatRegistered) return false;
  const sub = resolveSubscription(organization);
  if (!sub.isActive || !sub.modules.includes("etax")) return false;
  return (organization.enabledModules || []).includes("etax");
};

/** ร้านใช้โมดูลสมาชิก/แต้มได้ไหม (ตรรกะเดียวกับ hasModule ใน AppContext) */
export const orgUsesMembership = (organization) => {
  if (!organization) return false;
  const sub = resolveSubscription(organization);
  if (!sub.modules.includes("membership")) return false;
  return [...CORE_MODULE_KEYS, ...(organization.enabledModules || [])].includes("membership");
};

export const settleInvoicePayment = async ({ orgId, invoiceId, transactionId, amount, method = "qr", refNo = "", linkId = "", by = "", organization }) => {
  const tid = String(transactionId || "").trim();
  if (!tid) throw new Error("ไม่พบรหัสธุรกรรม จึงบันทึกการชำระเงินไม่ได้");

  const invoice = await invoiceRepo.get(orgId, invoiceId);
  if (!invoice) throw new Error("ไม่พบใบแจ้งหนี้");
  if (amount !== undefined && amount !== null && !sameAmount(amount, invoice.total)) {
    throw new Error("ยอดที่ชำระไม่ตรงกับยอดบิล (ระบบไม่รองรับการจ่ายบางส่วน) กรุณาติดต่อร้านพร้อมเลขอ้างอิง " + (refNo || tid));
  }

  // ใบแจ้งหนี้ชำระไปแล้วด้วยธุรกรรมอื่น/ช่องทางอื่น -> ห้ามออกใบเสร็จซ้ำ
  if (invoice.status === "paid" && invoice.paymentTransactionId !== tid) {
    throw new Error(`ใบแจ้งหนี้ ${invoice.docNo} ชำระไปแล้ว กรุณาติดต่อร้านพร้อมเลขอ้างอิง ${refNo || tid}`);
  }
  if (invoice.status === "cancelled") throw new Error(`ใบแจ้งหนี้ ${invoice.docNo} ถูกยกเลิกแล้ว กรุณาติดต่อร้านพร้อมเลขอ้างอิง ${refNo || tid}`);

  const paidAt = invoice.paidAt || new Date().toISOString();
  let current = invoice;
  if (invoice.status !== "paid") {
    current = await invoiceRepo.update(orgId, invoice.id, {
      status: "paid",
      paidAmount: invoice.total,
      paidAt,
      paymentMethod: method,
      paymentTransactionId: tid,
    });
  }

  let receipt = (await receiptRepo.all(orgId, (row) => row.transactionId === tid))[0] || null;
  const duplicate = !!receipt;
  if (!receipt) {
    const { docNo } = await nextDocNo({ orgId, docType: "receipt" });
    receipt = await receiptRepo.create(orgId, {
      docNo,
      invoiceId: invoice.id,
      invoiceNo: invoice.docNo,
      customerId: invoice.customerId,
      customerName: invoice.customerName,
      total: invoice.total,
      paidAt,
      paymentMethod: method,
      transactionId: tid,
      refNo,
      paymentLinkId: linkId || "",
    });
  }

  // ปิดลิงก์ชำระของใบแจ้งหนี้นี้ทั้งหมดที่ยัง active
  const links = await paymentLinkRepo.all(orgId, (row) => row.invoiceId === invoice.id && row.status === "active");
  for (const link of links) {
    await paymentLinkRepo.update(orgId, link.id, { status: "paid", paidAt, transactionId: tid, receiptId: receipt.id });
  }

  // e-Tax: สร้างฉบับร่างให้ร้านตรวจ (ไม่ออกเลขเอง) — พังไม่ทำให้การรับเงินล้ม
  let taxDraft = null;
  let warning = "";
  try {
    const org = organization || (await organizationRepo.get(null, orgId));
    if (orgUsesEtax(org) && !(await taxDocumentService.activeForInvoice(orgId, invoice.id))) {
      taxDraft = await taxDocumentService.createDraft({ orgId, organization: org, source: "receipt", invoice: current, receipt, by });
    }
  } catch (err) {
    warning = err.message;
  }

  // สะสมแต้มสมาชิก — กันซ้ำด้วย transactionId (ทำในทางเดียวกับใบเสร็จ; พังไม่ทำให้การรับเงินล้ม)
  let points = null;
  try {
    const org = organization || (await organizationRepo.get(null, orgId));
    if (invoice.customerId && orgUsesMembership(org)) {
      points = await earnPoints({ orgId, customerId: invoice.customerId, amount: invoice.total, refNo: invoice.docNo, transactionId: tid });
    }
  } catch (err) {
    warning = warning ? `${warning}; แต้มสะสม: ${err.message}` : `แต้มสะสม: ${err.message}`;
  }

  if (!duplicate) {
    await logActivity(orgId, {
      type: "payment",
      title: "ชำระเงินออนไลน์",
      detail: `${invoice.docNo} · ใบเสร็จ ${receipt.docNo}`,
      customerId: invoice.customerId,
      customerName: invoice.customerName,
      amount: invoice.total,
      by,
    });
  }

  return { invoice: current, receipt, taxDraft, duplicate, warning, points };
};

/** ชำระผ่านลิงก์: ลิงก์ผูกใบแจ้งหนี้ -> ปิดบิลเต็มรูปแบบ · ลิงก์เดี่ยว ๆ -> ปิดแค่ลิงก์ */
export const settleLinkPayment = async ({ orgId, link, transactionId, amount, refNo = "", by = "", organization }) => {
  const tid = String(transactionId || "").trim();
  if (!tid) throw new Error("ไม่พบรหัสธุรกรรม จึงบันทึกการชำระเงินไม่ได้");
  if (link.invoiceId) {
    return settleInvoicePayment({ orgId, invoiceId: link.invoiceId, transactionId: tid, amount, refNo, linkId: link.id, by, organization });
  }

  const fresh = (await paymentLinkRepo.get(orgId, link.id)) || link;
  if (amount !== undefined && amount !== null && !sameAmount(amount, fresh.amount)) {
    throw new Error("ยอดที่ชำระไม่ตรงกับยอดลิงก์ กรุณาติดต่อร้านพร้อมเลขอ้างอิง " + (refNo || tid));
  }
  if (fresh.status === "paid") {
    if (fresh.transactionId === tid) return { link: fresh, duplicate: true };
    throw new Error("ลิงก์นี้ชำระไปแล้ว กรุณาติดต่อร้านพร้อมเลขอ้างอิง " + (refNo || tid));
  }
  const paidAt = new Date().toISOString();
  const updated = await paymentLinkRepo.update(orgId, fresh.id, { status: "paid", paidAt, transactionId: tid });
  await logActivity(orgId, { type: "payment", title: "ชำระเงินผ่านลิงก์", detail: fresh.title || fresh.code, customerId: fresh.customerId, customerName: fresh.customerName, amount: fresh.amount, by });
  return { link: updated, duplicate: false };
};
